/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { FolderTreeNode } from '../models/FolderTreeNode';
import type { IngestionCredentialLinkResponse } from '../models/IngestionCredentialLinkResponse';
import type { IngestionCredentialRedeemRequest } from '../models/IngestionCredentialRedeemRequest';
import type { IngestionCredentialRedeemResponse } from '../models/IngestionCredentialRedeemResponse';
import type { IngestionFolderMappingCreate } from '../models/IngestionFolderMappingCreate';
import type { IngestionFolderMappingRead } from '../models/IngestionFolderMappingRead';
import type { IngestionFolderMappingUpdate } from '../models/IngestionFolderMappingUpdate';
import type { IngestionHistoryQuery } from '../models/IngestionHistoryQuery';
import type { IngestionRecordRead } from '../models/IngestionRecordRead';
import type { IngestionSetupRequest } from '../models/IngestionSetupRequest';
import type { IngestionTagMappingCreate } from '../models/IngestionTagMappingCreate';
import type { IngestionTagMappingRead } from '../models/IngestionTagMappingRead';
import type { IngestionTagMappingUpdate } from '../models/IngestionTagMappingUpdate';
import type { IngestionTenantConfigCreate } from '../models/IngestionTenantConfigCreate';
import type { IngestionTenantConfigRead } from '../models/IngestionTenantConfigRead';
import type { IngestionTenantConfigUpdate } from '../models/IngestionTenantConfigUpdate';
import type { RetryAllQuery } from '../models/RetryAllQuery';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class FileIngestionService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List All Tenant Configs
     * List all tenant ingestion configurations (staff only).
     * @returns IngestionTenantConfigRead Successful Response
     * @throws ApiError
     */
    public listAllTenantConfigs(): CancelablePromise<Array<IngestionTenantConfigRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/file-ingestion/admin/tenant-configs',
        });
    }

    /**
     * Create Tenant Config
     * Create a new tenant ingestion configuration (staff only).
     * @param requestBody
     * @returns IngestionTenantConfigRead Successful Response
     * @throws ApiError
     */
    public createTenantConfig(
        requestBody: IngestionTenantConfigCreate,
    ): CancelablePromise<IngestionTenantConfigRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/file-ingestion/admin/tenant-configs',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Tenant Config
     * Delete a tenant ingestion configuration (staff only).
     * @param configId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteTenantConfig(
        configId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/file-ingestion/admin/tenant-configs/{config_id}',
            path: {
                'config_id': configId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Redeem Credentials
     * Redeem a one-time credential token. No authentication required.
     * @param requestBody
     * @returns IngestionCredentialRedeemResponse Successful Response
     * @throws ApiError
     */
    public redeemCredentials(
        requestBody: IngestionCredentialRedeemRequest,
    ): CancelablePromise<IngestionCredentialRedeemResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/file-ingestion/redeem-credentials',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Setup Cluster Ingestion
     * Provision file ingestion and email a one-time credential link to the client (staff only).
     * @param clusterId
     * @param requestBody
     * @returns IngestionCredentialLinkResponse Successful Response
     * @throws ApiError
     */
    public setupClusterIngestion(
        clusterId: string,
        requestBody: IngestionSetupRequest,
    ): CancelablePromise<IngestionCredentialLinkResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/file-ingestion/setup',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Rotate Ingestion Credentials
     * Rotate IAM access key and email new credentials to the client (staff only).
     * @param clusterId
     * @param requestBody
     * @returns IngestionCredentialLinkResponse Successful Response
     * @throws ApiError
     */
    public rotateIngestionCredentials(
        clusterId: string,
        requestBody: IngestionSetupRequest,
    ): CancelablePromise<IngestionCredentialLinkResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/file-ingestion/rotate-credentials',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Tenant Config For Cluster
     * Get the ingestion configuration for a cluster.
     * @param clusterId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getTenantConfigForCluster(
        clusterId: string,
    ): CancelablePromise<(IngestionTenantConfigRead | null)> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/file-ingestion/tenant-config',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Tenant Config
     * Update an existing tenant ingestion configuration.
     * @param clusterId
     * @param configId
     * @param requestBody
     * @returns IngestionTenantConfigRead Successful Response
     * @throws ApiError
     */
    public updateTenantConfig(
        clusterId: string,
        configId: string,
        requestBody: IngestionTenantConfigUpdate,
    ): CancelablePromise<IngestionTenantConfigRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/clusters/{cluster_id}/file-ingestion/tenant-config/{config_id}',
            path: {
                'cluster_id': clusterId,
                'config_id': configId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Folder Mappings
     * List all folder → entity mappings for a cluster.
     * @param clusterId
     * @returns IngestionFolderMappingRead Successful Response
     * @throws ApiError
     */
    public listFolderMappings(
        clusterId: string,
    ): CancelablePromise<Array<IngestionFolderMappingRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/file-ingestion/folder-mappings',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Folder Mapping
     * Create a new folder → entity mapping.
     * @param clusterId
     * @param requestBody
     * @returns IngestionFolderMappingRead Successful Response
     * @throws ApiError
     */
    public createFolderMapping(
        clusterId: string,
        requestBody: IngestionFolderMappingCreate,
    ): CancelablePromise<IngestionFolderMappingRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/file-ingestion/folder-mappings',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Folder Mapping
     * Update an existing folder mapping.
     * @param clusterId
     * @param mappingId
     * @param requestBody
     * @returns IngestionFolderMappingRead Successful Response
     * @throws ApiError
     */
    public updateFolderMapping(
        clusterId: string,
        mappingId: string,
        requestBody: IngestionFolderMappingUpdate,
    ): CancelablePromise<IngestionFolderMappingRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/clusters/{cluster_id}/file-ingestion/folder-mappings/{mapping_id}',
            path: {
                'cluster_id': clusterId,
                'mapping_id': mappingId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Folder Mapping
     * Delete a folder mapping.
     * @param clusterId
     * @param mappingId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteFolderMapping(
        clusterId: string,
        mappingId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/clusters/{cluster_id}/file-ingestion/folder-mappings/{mapping_id}',
            path: {
                'cluster_id': clusterId,
                'mapping_id': mappingId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Folder Tree
     * Browse folders in the ingestion S3 bucket.
     *
     * Returns immediate child folders at the given prefix level with mapping info.
     * Used by the Folder Explorer UI for lazy-loading tree navigation.
     * @param clusterId
     * @param prefix Relative path prefix for lazy-loading subfolders
     * @returns FolderTreeNode Successful Response
     * @throws ApiError
     */
    public getFolderTree(
        clusterId: string,
        prefix: string = '',
    ): CancelablePromise<Array<FolderTreeNode>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/file-ingestion/folder-tree',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'prefix': prefix,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Tag Mappings
     * List all subfolder → document tag mappings for a cluster.
     * @param clusterId
     * @returns IngestionTagMappingRead Successful Response
     * @throws ApiError
     */
    public listTagMappings(
        clusterId: string,
    ): CancelablePromise<Array<IngestionTagMappingRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/file-ingestion/tag-mappings',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Tag Mapping
     * Create a new subfolder → document tag mapping.
     * @param clusterId
     * @param requestBody
     * @returns IngestionTagMappingRead Successful Response
     * @throws ApiError
     */
    public createTagMapping(
        clusterId: string,
        requestBody: IngestionTagMappingCreate,
    ): CancelablePromise<IngestionTagMappingRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/file-ingestion/tag-mappings',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Tag Mapping
     * Update an existing tag mapping.
     * @param clusterId
     * @param mappingId
     * @param requestBody
     * @returns IngestionTagMappingRead Successful Response
     * @throws ApiError
     */
    public updateTagMapping(
        clusterId: string,
        mappingId: string,
        requestBody: IngestionTagMappingUpdate,
    ): CancelablePromise<IngestionTagMappingRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/clusters/{cluster_id}/file-ingestion/tag-mappings/{mapping_id}',
            path: {
                'cluster_id': clusterId,
                'mapping_id': mappingId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Tag Mapping
     * Delete a tag mapping.
     * @param clusterId
     * @param mappingId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteTagMapping(
        clusterId: string,
        mappingId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/clusters/{cluster_id}/file-ingestion/tag-mappings/{mapping_id}',
            path: {
                'cluster_id': clusterId,
                'mapping_id': mappingId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Ingestion History
     * Query ingestion history for a cluster.
     * @param clusterId
     * @param requestBody
     * @returns IngestionRecordRead Successful Response
     * @throws ApiError
     */
    public listIngestionHistory(
        clusterId: string,
        requestBody: IngestionHistoryQuery,
    ): CancelablePromise<Array<IngestionRecordRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/file-ingestion/history',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Ingestion Stats
     * Get count of records by status for a cluster.
     * @param clusterId
     * @returns number Successful Response
     * @throws ApiError
     */
    public getIngestionStats(
        clusterId: string,
    ): CancelablePromise<Record<string, number>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/file-ingestion/history/stats',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Retry All Ingestion Records
     * Queue retry tasks for failed/quarantined records matching the given filters.
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public retryAllIngestionRecords(
        clusterId: string,
        requestBody?: RetryAllQuery,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/file-ingestion/history/retry-all',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Retry Ingestion Record
     * Retry a failed or quarantined ingestion record (synchronous).
     * @param clusterId
     * @param recordId
     * @returns IngestionRecordRead Successful Response
     * @throws ApiError
     */
    public retryIngestionRecord(
        clusterId: string,
        recordId: string,
    ): CancelablePromise<IngestionRecordRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/file-ingestion/history/{record_id}/retry',
            path: {
                'cluster_id': clusterId,
                'record_id': recordId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
